<template>
	<v-card class="embedded-forms-frame-card">
		<div class="embedded-forms-frame-wrap">
			<div v-if="!isReady || showAuthOverlay" class="embedded-forms-overlay">
				<v-progress-circular indeterminate color="primary" size="36" width="3" class="mb-3" />
				<div class="subheading">{{ overlayText }}</div>
			</div>

			<iframe
				ref="iframe"
				:src="iframeUrl"
				class="embedded-forms-iframe"
				frameborder="0"
				allow="clipboard-read; clipboard-write"
				@load="handleIframeLoad"
			/>
		</div>

		
	</v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import {
	buildEmbeddedFormsUrl,
	buildFormsAuthContext,
	getFormsAppOrigin,
	isTrustedFormsMessage,
	resolveMsalAccount,
	postMessageToFormsIframe
} from '@/common/formsEmbed';

export default {
	name: 'EmbeddedFormsAppFrame',
	props: {
		title: {
			type: String,
			default: 'Forms'
		},
		path: {
			type: String,
			required: true
		},
		query: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			isReady: false,
			showAuthOverlay: false,
			lastRoute: null,
			authFailureMessage: null,
			readyFallbackTimer: null
		};
	},
	computed: {
		...mapGetters({
			authUser: 'auth/user',
			msalInstance: 'auth/msalInstance',
			loggedInEmployee: 'loggedInEmployee'
		}),
		iframeUrl() {
			return buildEmbeddedFormsUrl(this.path, this.query);
		},
		authContext() {
			const authContext = buildFormsAuthContext(resolveMsalAccount(this.authUser, this.msalInstance));

			if (!authContext.loginHint && this.loggedInEmployee && this.loggedInEmployee.userEmail) {
				authContext.loginHint = this.loggedInEmployee.userEmail;
			}

			if (process.env.NODE_ENV !== 'production') {
				const resolvedAccount = resolveMsalAccount(this.authUser, this.msalInstance);
				console.log('[forms-embed] resolved MSAL account', resolvedAccount);
				console.log('[forms-embed] auth context payload', authContext);
			}

			return authContext;
		},
		overlayText() {
			if (this.showAuthOverlay) return 'Signing you in to forms...';
			return 'Loading forms...';
		}
	},
	watch: {
		iframeUrl() {
			this.resetState();
		}
	},
	created() {
		window.addEventListener('message', this.handleMessage);
	},
	beforeDestroy() {
		window.removeEventListener('message', this.handleMessage);
		this.clearReadyFallbackTimer();
	},
	methods: {
		resetState() {
			this.clearReadyFallbackTimer();
			this.isReady = false;
			this.showAuthOverlay = false;
			this.lastRoute = null;
			this.authFailureMessage = null;
		},
		handleIframeLoad() {
			this.sendAuthContext();
			this.scheduleReadyFallback();
		},
		scheduleReadyFallback() {
			this.clearReadyFallbackTimer();

			this.readyFallbackTimer = window.setTimeout(() => {
				if (!this.isReady && !this.showAuthOverlay && !this.authFailureMessage) {
					this.markReady(this.lastRoute);
				}
			}, 2500);
		},
		clearReadyFallbackTimer() {
			if (!this.readyFallbackTimer) return;
			window.clearTimeout(this.readyFallbackTimer);
			this.readyFallbackTimer = null;
		},
		markReady(payload) {
			this.clearReadyFallbackTimer();
			this.isReady = true;
			this.showAuthOverlay = false;
			this.lastRoute = payload || null;
			this.authFailureMessage = null;
		},
		sendAuthContext() {
			postMessageToFormsIframe(this.$refs.iframe, 'forms.authContext', this.authContext, getFormsAppOrigin());
		},
		async sendAuthToken(payload) {
			const requestId = payload && payload.requestId;
			if (!requestId) return;

			const responsePayload = { requestId };

			try {
				const token = await this.$store.dispatch('auth/acquireTokenSilent', {
					interactive: false,
					forceRefresh: !!payload.forceRefresh
				});

				if (!token || !token.accessToken) {
					throw new Error('No access token is available from the parent session.');
				}

				responsePayload.accessToken = token.accessToken;

				if (token.expiresOn) {
					const expiresOn = new Date(token.expiresOn).getTime();
					if (!Number.isNaN(expiresOn)) {
						responsePayload.expiresOn = expiresOn;
					}
				}
			} catch (error) {
				responsePayload.errorCode = error && error.errorCode ? error.errorCode : 'token_unavailable';
				responsePayload.message = error && error.message ? error.message : 'Unable to acquire a parent access token.';
			}

			postMessageToFormsIframe(this.$refs.iframe, 'forms.auth.tokenResponse', responsePayload, getFormsAppOrigin());
		},
		handleMessage(event) {
			if (!isTrustedFormsMessage(event, getFormsAppOrigin())) return;

			const { type, payload } = event.data;

			switch (type) {
				case 'forms.app.ready':
					this.markReady(payload);
					this.sendAuthContext();
					this.$emit('ready', payload);
					break;
				case 'forms.route.changed':
					this.markReady(payload);
					this.lastRoute = payload || null;
					this.$emit('routeChanged', payload);
					break;
				case 'forms.auth.required':
					this.clearReadyFallbackTimer();
					this.markReady(this.lastRoute);
					this.sendAuthContext();
					this.$emit('authRequired', payload);
					break;
				case 'forms.auth.tokenRequested':
					this.sendAuthToken(payload);
					break;
				case 'forms.auth.loginStarted':
					this.clearReadyFallbackTimer();
					this.showAuthOverlay = true;
					this.authFailureMessage = null;
					this.$emit('authLoginStarted', payload);
					break;
				case 'forms.auth.loginCompleted':
					this.markReady(this.lastRoute);
					this.$emit('authLoginCompleted', payload);
					break;
				case 'forms.auth.loginFailed':
					this.markReady(this.lastRoute);
					this.authFailureMessage = payload && payload.message ? payload.message : null;
					this.$emit('authLoginFailed', payload);
					break;
				default:
					break;
			}
		}
	}
};
</script>

<style scoped>
.embedded-forms-frame-card {
	display: flex;
	flex-direction: column;
	height: calc(100vh - 120px);
	min-height: 720px;
	border-radius: 12px;
	overflow: hidden;
}

.embedded-forms-frame-wrap {
	position: relative;
	flex: 1 1 auto;
	min-height: 0;
	background: #fff;
	display: flex;
}

.embedded-forms-iframe {
	flex: 1 1 auto;
	width: 100%;
	height: 100%;
	min-height: 0;
	border: 0;
	background: #fff;
}

.embedded-forms-overlay {
	position: absolute;
	inset: 0;
	background: rgba(255, 255, 255, 0.9);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-direction: column;
	z-index: 1;
}
</style>
